/**
 * documentStorageService
 * CRUD sobre localStorage para metadata de documentos de empleados.
 * NO almacena contenido de archivos — solo metadata (nombre, tamaño, tipo, etc.).
 *
 * Estructura de cada documento:
 * {
 *   id: string (UUID),
 *   fileName: string,
 *   fileSize: number (bytes),
 *   fileType: string (MIME),
 *   category: string,
 *   description: string,
 *   uploadedBy: string|number (employee ID),
 *   uploadedByName: string,
 *   uploadedAt: string (ISO 8601)
 * }
 */

import { LS_KEY_DOCUMENTS } from "../common/constants";

/** Lee el array completo de documentos de localStorage */
const readAll = () => {
  try {
    const raw = localStorage.getItem(LS_KEY_DOCUMENTS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/** Persiste el array completo en localStorage */
const writeAll = (docs) => {
  localStorage.setItem(LS_KEY_DOCUMENTS, JSON.stringify(docs));
};

/** UUID v4 simple (sin crypto.randomUUID en navegadores viejos) */
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });

/**
 * Obtiene todos los documentos almacenados.
 * @returns {Array} Lista de metadata de documentos
 */
export const getDocuments = () => readAll();

/**
 * Agrega un documento (solo metadata).
 *
 * @param {{ fileName: string, fileSize: number, fileType: string, category?: string, description?: string, uploadedBy: string|number, uploadedByName: string }} metadata
 * @returns {Object} El documento creado con id y timestamp
 */
export const addDocument = (metadata) => {
  const docs = readAll();
  const doc = {
    id: uuid(),
    fileName: metadata.fileName,
    fileSize: metadata.fileSize,
    fileType: metadata.fileType,
    category: metadata.category || "Other",
    description: metadata.description || "",
    uploadedBy: metadata.uploadedBy,
    uploadedByName: metadata.uploadedByName,
    uploadedAt: new Date().toISOString(),
  };
  docs.unshift(doc);
  writeAll(docs);
  return doc;
};

/**
 * Actualiza category y/o description de un documento.
 *
 * @param {string} id - UUID del documento
 * @param {{ category?: string, description?: string }} updates
 * @returns {Object|null} El documento actualizado o null si no existe
 */
export const updateDocument = (id, updates) => {
  const docs = readAll();
  const idx = docs.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  if (updates.category !== undefined) docs[idx].category = updates.category;
  if (updates.description !== undefined) docs[idx].description = updates.description;

  writeAll(docs);
  return docs[idx];
};

/**
 * Elimina un documento por ID.
 *
 * @param {string} id - UUID del documento
 * @returns {boolean} true si se eliminó, false si no existía
 */
export const removeDocument = (id) => {
  const docs = readAll();
  const filtered = docs.filter((d) => d.id !== id);
  if (filtered.length === docs.length) return false;
  writeAll(filtered);
  return true;
};
